import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface BulkIssueMaterialModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface Lookup {
  id: number;
  name: string;
}

interface MaterialLine {
  key: number;
  courseId: number | "";
  assemblyId: number | "";
  componentId: number | "";
  quantity: string;
  discount: string;
  assemblies: Lookup[];
  components: Lookup[];
}

const today = () => new Date().toISOString().slice(0, 10);
let lineKeySeq = 1;
const newLine = (): MaterialLine => ({
  key: lineKeySeq++,
  courseId: "",
  assemblyId: "",
  componentId: "",
  quantity: "1",
  discount: "",
  assemblies: [],
  components: [],
});

const cellInput: React.CSSProperties = {
  width: "100%",
  minWidth: 90,
  height: 28,
  padding: "0 6px",
  fontSize: "0.8rem",
  border: "1px solid var(--border-strong)",
  borderRadius: 4,
};

const BulkIssueMaterialModal: React.FC<BulkIssueMaterialModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const { enrollments } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const [godowns, setGodowns] = useState<Lookup[]>([]);
  const [years, setYears] = useState<Lookup[]>([]);
  const [users, setUsers] = useState<Lookup[]>([]);
  const [courses, setCourses] = useState<Lookup[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [indentNumber, setIndentNumber] = useState("AUTO");
  const [shipmentDate, setShipmentDate] = useState(today());
  const [fromGodownId, setFromGodownId] = useState<number | "">("");
  const [yearId, setYearId] = useState<number | "">("");
  const [raisedByUserId, setRaisedByUserId] = useState<number | "">("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState<MaterialLine[]>([newLine()]);

  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    setLines([newLine()]);
    setIndentNumber("AUTO");
    setShipmentDate(today());
    setSummary(null);
    EnrollmentService.getIssueMaterialLookups()
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data?.data || {};
        setGodowns(d.godowns || []);
        setYears(d.years || []);
        setUsers(d.users || []);
        setCourses(d.courses || []);
        if (d.userId) setRaisedByUserId(Number(d.userId));
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const updateLine = (key: number, patch: Partial<MaterialLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const onCourseChange = async (key: number, courseId: number | "") => {
    updateLine(key, { courseId, assemblyId: "", componentId: "", assemblies: [], components: [] });
    if (courseId) {
      try {
        const resp = await EnrollmentService.getMaterialAssemblies(
          Number(courseId),
          yearId ? Number(yearId) : 0,
        );
        updateLine(key, { assemblies: resp.data?.data?.assemblies || [] });
      } catch {
        /* ignore — leave empty */
      }
    }
  };

  const onAssemblyChange = async (key: number, assemblyId: number | "") => {
    updateLine(key, { assemblyId, componentId: "", components: [] });
    if (assemblyId) {
      try {
        const resp = await EnrollmentService.getMaterialComponents(Number(assemblyId));
        updateLine(key, { components: resp.data?.data?.components || [] });
      } catch {
        /* ignore */
      }
    }
  };

  const addRow = () => setLines((prev) => [...prev, newLine()]);
  const removeRow = (key: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const handleClose = () => {
    setFromGodownId("");
    setYearId("");
    setRemarks("");
    setLines([newLine()]);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!fromGodownId) {
      setError("Select Issued At.");
      return;
    }
    if (!shipmentDate) {
      setError("Issue Date is required.");
      return;
    }
    const payloadLines = lines
      .filter((l) => l.assemblyId)
      .map((l) => ({
        courseId: l.courseId ? Number(l.courseId) : null,
        assemblyId: Number(l.assemblyId),
        componentId: l.componentId ? Number(l.componentId) : null,
        quantity: Number(l.quantity) > 0 ? Number(l.quantity) : 1,
        discount: Number(l.discount) || 0,
      }));
    if (payloadLines.length === 0) {
      setError("Add at least one material line (pick an assembly).");
      return;
    }
    if (enrollments.length === 0) {
      setError("The selected students have no enrollments.");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkIssueMaterial({
        enrollmentIds: enrollments.map((e) => e.enrollmentId),
        indentNumber: indentNumber || "AUTO",
        shipmentDate,
        fromGodownId: Number(fromGodownId),
        yearId: yearId ? Number(yearId) : null,
        raisedByUserId: raisedByUserId ? Number(raisedByUserId) : null,
        remarks: remarks.trim() || null,
        lines: payloadLines,
      });
      setSummary(resp.data?.data?.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to issue material");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600 };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {summary ? "Issue Material — Results" : "Issue Material"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {summary ? (
          <div style={{ display: "flex", gap: 12, fontSize: 13, color: "var(--text-strong)" }}>
            <span>
              <strong>{summary.total ?? 0}</strong> processed
            </span>
            <span style={{ color: "var(--status-success-text)" }}>
              <strong>{summary.issued ?? 0}</strong> issued
            </span>
            <span style={{ color: "var(--status-info-text)" }}>
              <strong>{summary.skipped ?? 0}</strong> skipped
            </span>
            <span style={{ color: "var(--status-danger)" }}>
              <strong>{summary.failed ?? 0}</strong> failed
            </span>
          </div>
        ) : loadingLookups ? (
          <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
            <i className="fa fa-spinner fa-spin" /> Loading options…
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {/* Header fields (left) */}
              <div style={{ flex: "1 1 380px" }}>
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>Acknowledgement No.</Form.Label>
                  <Form.Control
                    size="sm"
                    value={indentNumber}
                    onChange={(e) => setIndentNumber(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>
                    Issue Date <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={shipmentDate}
                    onChange={(e) => setShipmentDate(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>
                    Issued At <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={fromGodownId}
                    onChange={(e) => setFromGodownId(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">--Select--</option>
                    {godowns.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>Year</Form.Label>
                  <Form.Select
                    size="sm"
                    value={yearId}
                    onChange={(e) => setYearId(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">--Select--</option>
                    {years.map((y) => (
                      <option key={y.id} value={y.id}>
                        {y.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>Issued By</Form.Label>
                  <Form.Select
                    size="sm"
                    value={raisedByUserId}
                    onChange={(e) => setRaisedByUserId(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">--Select--</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Remarks"
                  />
                </Form.Group>
              </div>

              {/* Selected Students (right) */}
              <div style={{ flex: "1 1 320px" }}>
                <div style={{ ...labelStyle, marginBottom: 8 }}>Selected Students</div>
                <div
                  style={{
                    maxHeight: 260,
                    overflow: "auto",
                    border: "1px solid var(--border-default)",
                    borderRadius: 6,
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={headerCellStyle}>Sl no.</th>
                        <th style={headerCellStyle}>Student Name/Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.map((e, i) => (
                        <tr key={e.enrollmentId}>
                          <td style={bodyCellStyle}>{i + 1}</td>
                          <td style={bodyCellStyle}>
                            {e.studentName}
                            {e.studentCode ? ` ( ${e.studentCode} )` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Material line grid */}
            <div
              style={{
                marginTop: 12,
                overflowX: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Sl no.</th>
                    <th style={headerCellStyle}>Course</th>
                    <th style={headerCellStyle}>Assembly</th>
                    <th style={headerCellStyle}>Component</th>
                    <th style={headerCellStyle}>Quantity</th>
                    <th style={headerCellStyle}>Discount</th>
                    <th style={{ ...headerCellStyle, textAlign: "center" }}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.key}>
                      <td style={bodyCellStyle}>{i + 1}</td>
                      <td style={{ ...bodyCellStyle, minWidth: 170 }}>
                        <select
                          value={l.courseId}
                          onChange={(e) =>
                            onCourseChange(l.key, e.target.value ? Number(e.target.value) : "")
                          }
                          style={cellInput}
                        >
                          <option value="">-- All --</option>
                          {courses.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...bodyCellStyle, minWidth: 180 }}>
                        <select
                          value={l.assemblyId}
                          onChange={(e) =>
                            onAssemblyChange(l.key, e.target.value ? Number(e.target.value) : "")
                          }
                          style={cellInput}
                        >
                          <option value="">-- Select --</option>
                          {l.assemblies.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...bodyCellStyle, minWidth: 180 }}>
                        <select
                          value={l.componentId}
                          onChange={(e) =>
                            updateLine(l.key, {
                              componentId: e.target.value ? Number(e.target.value) : "",
                            })
                          }
                          style={cellInput}
                        >
                          <option value="">-- All components --</option>
                          {l.components.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...bodyCellStyle, width: 80 }}>
                        <input
                          type="number"
                          value={l.quantity}
                          onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                          style={{ ...cellInput, width: 70 }}
                        />
                      </td>
                      <td style={{ ...bodyCellStyle, width: 90 }}>
                        <input
                          type="number"
                          value={l.discount}
                          onChange={(e) => updateLine(l.key, { discount: e.target.value })}
                          style={{ ...cellInput, width: 80 }}
                        />
                      </td>
                      <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => removeRow(l.key)}
                          title="Remove"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "var(--status-danger)",
                            cursor: "pointer",
                          }}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8 }}>
              <Button variant="secondary" size="sm" onClick={addRow}>
                <i className="fa fa-plus" /> Add Row
              </Button>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {summary ? (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || loadingLookups || !fromGodownId}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Issuing…
                </>
              ) : (
                <>Issue Material</>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkIssueMaterialModal;
